import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import { RepartitionCard } from '@components/charts/analytics/RepartitionBar'
import {
  figureValue,
  formatPercent,
  lines,
} from '@components/charts/membership/revenue/figures'
import SectionHead from '@components/charts/membership/revenue/SectionHead'
import useRevenueStatistics from '@components/charts/membership/revenue/useRevenueStatistics'
import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchMembershipAnalyticsGraphParams,
  MembershipAnalyticsGraphRepartitionItem,
} from '@models/membership/MembershipAnalyticsGraph'
import { ResponseMembershipPromotionAnalyticsStatistics } from '@models/membership/MembershipAnalyticsStatistics'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { analyticsCurrency } from '@utils/currencyFormatter'
import { Col, Flex, Row, Typography } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

const { Text } = Typography

/** "Promotion & discounts" (Figma 993:2461): discounts granted, rate and repartition. */
const PromotionDiscounts = () => {
  const {
    selectedDates,
    selectedMembershipPrograms,
    isMembershipProgramReady,
  } = useAnalytics()

  const {
    data: statsData,
    isLoading: isStatsLoading,
    error: statsError,
  } = useRevenueStatistics<ResponseMembershipPromotionAnalyticsStatistics>(
    'membership-promotion',
    true
  )

  const [repartitionData, setRepartitionData] = useState<
    MembershipAnalyticsGraphRepartitionItem[]
  >([])
  const [isRepartitionLoading, setIsRepartitionLoading] = useState(true)
  const [repartitionError, setRepartitionError] = useState<string>()
  const latest = useRef(0)

  useEffect(() => {
    if (!isMembershipProgramReady || !selectedDates) return

    const params: FetchMembershipAnalyticsGraphParams = {
      entity: 'membership',
      kind: 'discount-repartition',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
      programIds: selectedMembershipPrograms,
    }

    const requestId = ++latest.current
    setIsRepartitionLoading(true)
    setRepartitionError(undefined)

    fetchAnalyticsGraph<MembershipAnalyticsGraphRepartitionItem[]>(params)
      .then(({ data }) => {
        if (requestId !== latest.current) return
        setRepartitionData(data || [])
        if (!data) setRepartitionError('No data available.')
      })
      .catch((e) => {
        if (requestId !== latest.current) return
        console.error('Error fetching data:', e)
        setRepartitionError('An error occurred while fetching data.')
      })
      .finally(() => {
        if (requestId === latest.current) setIsRepartitionLoading(false)
      })
  }, [selectedDates, selectedMembershipPrograms, isMembershipProgramReady])

  const since = statsData?.discountTrackedSince
    ? dayjs(statsData.discountTrackedSince).format('D MMM YYYY')
    : undefined
  const sinceLabel = since ? `since ${since}` : undefined
  const couponsUsed = statsData?.couponsUsed?.value
  const tracked = sinceLabel
    ? `Only payments ${sinceLabel} record their price before discount.`
    : 'Only recent payments record their price before discount.'

  return (
    <Flex vertical gap={24}>
      <SectionHead
        title="Promotion & discounts"
        caption="Follows the selected date range"
      />
      {/* `align="stretch"`: both KPI cards grow to the repartition card's height. */}
      <Row gutter={[24, 24]} align="stretch">
        <Col span={6}>
          <AnalyticsStatistic
            variant="overview"
            title="Discounts granted"
            tooltip={`Total discount given by coupons over the period. ${tracked}`}
            value={
              statsData &&
              analyticsCurrency(
                statsData.discountsGranted?.value,
                false,
                statsData.currency
              )
            }
            secondary={lines(
              typeof couponsUsed === 'number' &&
                `${couponsUsed} coupons used${sinceLabel ? ` · ${sinceLabel}` : ''}`
            )}
            isLoading={isStatsLoading}
          />
        </Col>
        <Col span={6}>
          <AnalyticsStatistic
            variant="overview"
            title="Discount rate"
            tooltip="Discounts as a share of the price before discount."
            value={formatPercent(figureValue(statsData?.discountRate))}
            isLoading={isStatsLoading}
          />
        </Col>
        <Col span={12}>
          <RepartitionCard
            title="Discount repartition"
            // One slice per coupon code: keep the legend readable.
            maxLegendItems={6}
            data={repartitionData}
            isLoading={isRepartitionLoading}
            error={repartitionError}
          />
        </Col>
      </Row>
      {statsError && <Text type="danger">{statsError}</Text>}
    </Flex>
  )
}

export default PromotionDiscounts
